import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import { store } from '../store/store';
import { setAccessToken, startRefreshingToken, stopRefreshingToken } from '../store/authSlice';
import { performLogout } from '../utils/authLogout';
import { motivoDaRespostaRefresh, MotivoLogout } from '../utils/authAviso';

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

/**
 * Cliente HTTP central do frontend. Fonte ÚNICA de token: o `accessToken` do
 * Redux (persistido). Todas as chamadas autenticadas devem passar por aqui.
 *
 * - Interceptor de request: injeta `Authorization: Bearer <accessToken do Redux>`.
 * - Interceptor de response: no 401, tenta 1 refresh (single-flight) e repete a
 *   chamada uma vez.
 *   - refresh OK             → repete a chamada original;
 *   - refresh 401/403        → sessão morreu: logout com aviso na tela de login;
 *   - refresh indisponível   → NÃO desloga (ver `refreshOnce` abaixo).
 *
 * Rotas públicas de auth (login/signup/logout/refresh) NÃO usam este cliente —
 * seguem em `axios` cru — para que uma falha 401 delas não dispare o logout.
 */
export const http = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true,
});

// REQUEST: injeta o Bearer do Redux (fonte única de token).
http.interceptors.request.use((config) => {
  const token = store.getState().auth.accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Resultado classificado de uma tentativa de renovação (at-22).
 *
 * A versão anterior devolvia `string | null` com um `.catch(() => null)`: timeout,
 * queda de 4G, 5xx e backend recompilando viravam todos "sessão expirada" e
 * deslogavam um usuário com sessão perfeitamente válida — a causa mais provável
 * do "jogado pro alto do nada" relatado em campo. Agora a falha é classificada:
 * só 401/403 (o servidor DIZENDO que a sessão morreu) desloga.
 */
export type ResultadoRefresh =
  | { ok: true; token: string }
  | { ok: false; motivo: 'expirada'; motivoLogout: MotivoLogout }
  | { ok: false; motivo: 'indisponivel' };

/** Sem isso uma renovação pendurada deixaria `isRefreshing` ligado para sempre. */
const TIMEOUT_REFRESH_MS = 20_000;

// REFRESH single-flight: uma renovação por vez; chamadas concorrentes (o
// interceptor de N chamadas em 401 e o refresh proativo do App) esperam a MESMA
// Promise em vez de disparar N refreshes concorrentes — com rotação de refresh
// token no backend, refreshes paralelos derrubam a sessão de quem perde a corrida.
let refreshPromise: Promise<ResultadoRefresh> | null = null;

function classificarFalha(error: unknown): ResultadoRefresh {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;

  // 401/403 = o servidor recusou o refresh token: sessão realmente encerrada.
  if (status === 401 || status === 403) {
    const data = axios.isAxiosError(error) ? error.response?.data : undefined;
    return { ok: false, motivo: 'expirada', motivoLogout: motivoDaRespostaRefresh(data) };
  }

  // Sem `response` (rede/timeout/CORS), 5xx, ou qualquer outro status: problema
  // de infraestrutura, não de sessão. Mantemos a sessão de pé.
  return { ok: false, motivo: 'indisponivel' };
}

/**
 * Renova o access token. Único motor de refresh da aplicação — o refresh
 * proativo do `App.tsx` também passa por aqui, para compartilhar o single-flight.
 *
 * Sinaliza `isRefreshing` no store durante a tentativa (o `PrivateRoute` usa
 * isso para aguardar em vez de mandar o usuário para o login).
 */
export async function refreshOnce(): Promise<ResultadoRefresh> {
  if (!refreshPromise) {
    store.dispatch(startRefreshingToken());

    refreshPromise = axios // axios "cru" para NÃO recorrer no interceptor
      .post<{ accessToken?: string }>(
        `${process.env.REACT_APP_BACKEND_URL}/auth/refresh`,
        {},
        { withCredentials: true, timeout: TIMEOUT_REFRESH_MS },
      )
      .then((r): ResultadoRefresh => {
        const novoToken = r.data?.accessToken;
        // 200 sem token é contrato quebrado, não sessão expirada: não desloga.
        return novoToken
          ? { ok: true, token: novoToken }
          : { ok: false, motivo: 'indisponivel' };
      })
      .catch(classificarFalha)
      .then((resultado) => {
        // TODOS os dispatches do desfecho ficam neste único bloco síncrono, de
        // propósito: o React 18 os agrupa em UM re-render. Se `isRefreshing`
        // caísse para false num render e o `sessaoIndisponivel` só chegasse no
        // render seguinte, o `PrivateRoute` veria "token expirado + ninguém
        // renovando" no meio e deslogaria o usuário offline.
        if (resultado.ok) store.dispatch(setAccessToken(resultado.token));
        store.dispatch(
          stopRefreshingToken({
            indisponivel: !resultado.ok && resultado.motivo === 'indisponivel',
          }),
        );
        return resultado;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

/**
 * Avisa que a conexão falhou SEM deslogar. Trocar "deslogado sem aviso" por
 * "tela travada sem aviso" seria o mesmo defeito com outra cara — o usuário
 * precisa saber que foi a internet, e que a sessão continua ativa.
 *
 * `toastId` fixo: N chamadas em 401 simultâneas geram um único toast (a
 * react-toastify ignora um id já ativo). O container único vive em `index.tsx`
 * (at-19) — aqui só se chama `toast()`.
 */
export function avisarConexaoIndisponivel(): void {
  toast.warn(
    'Sem conexão com o servidor. Sua sessão continua ativa — verifique a internet e tente novamente.',
    { toastId: 'sessao-conexao-indisponivel', autoClose: 6000 },
  );
}

// RESPONSE: no 401, tenta 1 refresh e repete a chamada.
http.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;
    const isRefreshCall = original?.url?.includes('/auth/refresh');

    if (status === 401 && original && !original._retried && !isRefreshCall) {
      original._retried = true;
      const resultado = await refreshOnce();

      if (resultado.ok) {
        original.headers = original.headers ?? {};
        original.headers.Authorization = `Bearer ${resultado.token}`;
        return http(original); // repete a chamada original
      }

      if (resultado.motivo === 'expirada') {
        // O servidor recusou o refresh: sessão encerrada de verdade.
        void performLogout(resultado.motivoLogout);
      } else {
        // Rede/timeout/5xx: a sessão pode estar ótima. Avisa e mantém o usuário
        // logado; a próxima chamada (ou o refresh proativo) tenta de novo.
        avisarConexaoIndisponivel();
      }
    }

    return Promise.reject(error);
  },
);
